/* eslint-disable react/require-default-props */
import React, { forwardRef } from 'react';

import { InputAdornment, TextField as MuiTextField } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';
import clsx from 'clsx';

export type TextFieldStatusType = 'default' | 'disabled' | 'error' | 'success';

export type TextFieldProps = MuiTextFieldProps & {
  endIcon?: JSX.Element;
  helperText?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  startIcon?: JSX.Element;
  status?: TextFieldStatusType;
  type?: 'email' | 'tel' | 'text' | 'number';
};

const useStyles = makeStyles((theme: Theme) => ({
  error: {
    color: theme.palette.error.main,

    '& svg': {
      color: theme.palette.error.main,
    },
  },
  hintMessageSpaceHolder: {
    borderBottom: '3px solid transparent',
    paddingBottom: theme.spacing(2),
  },
  textField: {
    '& .MuiInputBase-adornedStart:not(.Mui-error)': {
      border: `1px solid ${theme.palette.grey[400]}`,
    },

    '& .MuiInputBase-adornedStart': {
      paddingLeft: theme.spacing(2),

      '& input': {
        border: 'none',
      },
    },

    '& .MuiInputBase-adornedEnd': {
      paddingRight: theme.spacing(1.25),
    },

    '& .MuiFormLabel-root, .MuiFormHelperText-root': {
      padding: 0,
    },
    '& .MuiFormHelperText-root': {
      fontSize: theme.typography.pxToRem(12),
    },
  },
  success: {
    color: theme.palette.success.main,

    '& svg': {
      color: theme.palette.success.main,
    },

    '& .MuiInput-root': {
      borderColor: theme.palette.success.main,
    },
  },
}));

const TextField = forwardRef((props: TextFieldProps, ref: any) => {
  const {
    endIcon = null,
    helperText = '',
    label = '',
    placeholder = '',
    required = false,
    startIcon = null,
    status = 'default',
    type = 'text',
    ...rest
  } = props;
  const classes = useStyles();

  const textFieldStyles = clsx(
    classes.textField,
    'fd-input',
    status === 'error' && classes.error,
    status === 'success' && 'success',
    status === 'success' && classes.success,
    !helperText && classes.hintMessageSpaceHolder,
  );

  const startAdornment = startIcon ? (
    <InputAdornment position="start">{startIcon}</InputAdornment>
  ) : null;
  const endAdornment = endIcon ? (
    <InputAdornment position="end">{endIcon}</InputAdornment>
  ) : null;

  return (
    <MuiTextField
      className={textFieldStyles}
      disabled={status === 'disabled'}
      error={status === 'error'}
      fullWidth
      helperText={helperText}
      InputProps={{
        startAdornment,
        endAdornment,
      }}
      label={label}
      placeholder={placeholder}
      ref={ref}
      required={required}
      type={type}
      {...rest}
    />
  );
});

TextField.displayName = 'TextField';
export { TextField };
